import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FiCalendar, FiX } from "react-icons/fi";
import usePopoverPosition from "../../../hooks/usePopoverPosition";
// [R23] Nagłówek z listą miesięcy/lat, skokiem ±10 lat i wpisem daty z klawiatury — wspólny z DateTimePicker.
import NaglowekKalendarza from "./NaglowekKalendarza";
// [obszar 3, 2026-09-17] Granice lat 1910–2099 — węższe z (granica, min/max pola) wygrywa.
import { granice, ograniczRokWidoku } from "./GraniceDaty";
import styles from "./DatePicker.module.css";
import { useWarstwaNadModalem } from "../../SideDrawer/warstwaNadModalem";

const WEEKDAYS_PL = ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Ndz"];

function toDateStr(d) {
    if (!d) return "";
    const dt = typeof d === "string" ? new Date(d) : d;
    if (isNaN(dt.getTime())) return "";
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}

function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() &&
           a.getMonth() === b.getMonth() &&
           a.getDate() === b.getDate();
}

function formatDisplay(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" });
}

/**
 * 🔴 WARSTWA KALENDARZA — `nadModalem` [obszar C, polecenie właściciela 2026-09-11].
 *
 * Kalendarz portaluje się do <body> na warstwę --z-datepicker (10001). Szuflada otwarta Z WNĘTRZA
 * okna modalnego (SideDrawer `nadModalem`) stoi na --z-drawer-over-modal (10011) — czyli WYŻEJ.
 * Kalendarz otwarty z takiej szuflady wysuwał się POD jej panelem i nie dało się wybrać daty;
 * formularz etapów realizacji uciekał z tego powodu do natywnego `input[type=datetime-local]`.
 *
 * ⚠ PODNIESIENIE --z-datepicker GLOBALNIE JEST ZŁYM LEKARSTWEM: ten token niesie kalendarze na
 * ~90 ekranach, a większość z nich stoi na zwykłej stronie albo w szufladzie, którą MUSI dać się
 * przykryć oknem modalnym. Podniesienie wszystkich zamieniłoby jedną usterkę na wiele — dokładnie
 * ten błąd opisuje komentarz przy --z-drawer-over-modal w styles/zindex.css.
 *
 * Dlatego wybór warstwy jest ŚWIADOMY I MIEJSCOWY, wzorem SideDrawer.nadModalem: domyślnie nic się
 * nie zmienia, a pole w szufladzie nad modalem samo mówi, że potrzebuje --z-drawer-over-modal-dropdown
 * (10012) — warstwy, która ISTNIEJE w skali dokładnie po to („lista rozwijana WEWNĄTRZ takiej szuflady
 * nie chowa się pod jej własnym panelem"). Nie zakładamy nowej warstwy, korzystamy z nazwanej.
 *
 * ⚠ Zmiennej CSS z `.drawerNadModalem` (--sd-dropdown-layer) NIE DA SIĘ tu użyć: dziedziczenie zmiennych
 * idzie po DRZEWIE DOM, a kalendarz jest w portalu przy <body>, poza szufladą. Stąd prop, nie zmienna.
 */
export default function DatePicker({
    value,
    onChange,
    placeholder = "Wybierz datę",
    min,
    max,
    clearable = true,
    disabled = false,
    className = "",
    /* Bez wartości domyślnej: brak propsa = decyduje kontekst WarstwaNadModalem (patrz niżej). */
    nadModalem: nadModalemJawny,
    /* Wyzwalacz kalendarza to `<button>`, więc jego nazwą dla czytnika ekranu jest WŁASNA TREŚĆ,
       czyli wybrana data — bez nazwy pola brzmi to jak „11 września 2026" bez odpowiedzi na pytanie
       „czego to data". Gdy pole ma etykietę na ekranie, podajemy ją tutaj; nazwa składa się wtedy
       z etykiety I wartości, żeby nie zgubić żadnej z dwóch informacji. */
    ariaLabel = null,
    /* [U11 2026-09-25] Pole z błędem (`components/common/PoleFormularza`) przekazuje identyfikator
       i stan błędu przez `cloneElement`. Wyzwalaczem jest `<button>`, więc to na nim muszą stanąć
       `id` (etykieta `for`) i `aria-describedby` (komunikat pod polem) — inaczej czytnik ekranu przeczyta
       datę bez powodu odmowy. `aria-invalid` NIE przysługuje roli button (jsx-a11y), więc stan błędu idzie
       atrybutem `data-bledne`, po którym PoleFormularza maluje czerwoną ramkę. */
    id,
    "aria-invalid": ariaInvalid,
    "aria-describedby": ariaDescribedBy,
}) {
    /* [2026-10-01] Jawne true/false wygrywa; bez propsa pole w szufladzie nad oknem (albo w oknie objętym
       SzufladyNadModalem) samo staje na --z-drawer-over-modal-dropdown. Zwykła strona: kontekst false, bez zmian. */
    const nadModalem = useWarstwaNadModalem(nadModalemJawny);
    const [open, setOpen] = useState(false);
    const [viewDate, setViewDate] = useState(() => {
        if (value) {
            const d = new Date(value);
            // [obszar 3] Wartość zapisana z rokiem spoza granic (zrzut właściciela: 3423) nie wywozi
            // kalendarza w rok, którego lista lat nawet nie zna — widok siada na krawędzi zakresu.
            return isNaN(d.getTime()) ? new Date() : ograniczRokWidoku(d);
        }
        return new Date();
    });

    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    // Kalendarz stoi PRZY polu (portal + pomiar przed malowaniem) — patrz hooks/usePopoverPosition.
    const { style: popStyle } = usePopoverPosition(open, triggerRef, dropdownRef, { minHeight: 240, fit: true });

    // Sync viewDate when value changes
    useEffect(() => {
        if (value) {
            const d = new Date(value);
            if (!isNaN(d.getTime())) {
                setViewDate(ograniczRokWidoku(d));
            }
        }
    }, [value]);

    const toggle = () => {
        if (disabled) return;
        setOpen(p => !p);
    };

    const close = () => setOpen(false);

    const selectDay = (day) => {
        const str = toDateStr(day);
        onChange(str);
        close();
    };

    // [R23] Pełna data z klawiatury (dd.mm.rrrr): ta sama wartość, którą dałby klik w dzień — ale kalendarz
    // zostaje otwarty, żeby było widać, dokąd skoczył; Enter w polu wpisu zamyka go (onZatwierdz).
    // Wpis NIE omija min/max: dzień, którego nie da się kliknąć, nie przejdzie też z klawiatury (oddaje false).
    const wpisanaData = (str) => {
        const wpisany = new Date(str + "T00:00:00");
        setViewDate(ograniczRokWidoku(wpisany));
        if (isDayDisabled(wpisany)) return false;
        onChange(str);
        return true;
    };

    const klawiszKalendarza = (e) => {
        if (e.key === "Escape") { e.stopPropagation(); close(); }
    };

    const goToday = (e) => {
        e.stopPropagation();
        const today = new Date();
        setViewDate(today);
        // [R22, poprawka po przeglądzie] „Dzisiaj” spoza min/max tylko pokazuje bieżący miesiąc — nie wybiera dnia,
        // którego nie da się kliknąć ani wpisać.
        if (isDayDisabled(today)) return;
        selectDay(today);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange("");
        close();
    };

    /* [obszar 3, 2026-09-17] Zakres pola to PRZECIĘCIE min/max podanego przez stronę z granicami
       1910–2099 — węższe wygrywa. Sprawdzenie stoi w jednym miejscu, przez które idą WSZYSTKIE drogi
       ustawienia dnia: klik w komórkę, „Dzisiaj" i wpis z klawiatury. Dlatego rok 3423 nie da się
       ustawić także wtedy, gdy przyjdzie z zewnątrz albo z pominięciem nagłówka. */
    const zakres = granice(min, max);
    const isDayDisabled = (d) => {
        const minD = new Date(zakres.min + "T00:00:00");
        if (d < minD && !isSameDay(d, minD)) return true;
        const maxD = new Date(zakres.max + "T00:00:00");
        if (d > maxD && !isSameDay(d, maxD)) return true;
        return false;
    };

    // Build calendar grid
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startDow = firstDay.getDay();
    if (startDow === 0) startDow = 7; // Mon=1, Sun=7
    const today = new Date();

    const cells = [];
    // Previous month fill
    const prevMonthLast = new Date(year, month, 0);
    for (let i = startDow - 2; i >= 0; i--) {
        const d = new Date(year, month - 1, prevMonthLast.getDate() - i);
        cells.push({ date: d, otherMonth: true });
    }
    // Current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
        cells.push({ date: new Date(year, month, i), otherMonth: false });
    }
    // Next month fill
    const remaining = 42 - cells.length;
    for (let i = 1; i <= remaining; i++) {
        cells.push({ date: new Date(year, month + 1, i), otherMonth: true });
    }

    const selectedDate = value ? new Date(value + "T00:00:00") : null;

    const dropdown = open ? createPortal(
        <>
            <div
                className={`${styles.overlay} ${nadModalem ? styles.overlayNadModalem : ""}`}
                onClick={close}
            />
            <div
                className={`${styles.dropdown} ${nadModalem ? styles.dropdownNadModalem : ""}`}
                ref={dropdownRef}
                style={popStyle}
                onClick={e => e.stopPropagation()}
                onKeyDown={klawiszKalendarza}
            >
                {/* Nagłówek: miesiąc / rok z list, ±10 lat, wpis daty z klawiatury (R23) */}
                <NaglowekKalendarza
                    viewDate={viewDate}
                    onViewDate={setViewDate}
                    dataStr={value || ""}
                    onWpisanaData={wpisanaData}
                    onZatwierdz={close}
                />

                {/* Weekday labels */}
                <div className={styles.weekdays}>
                    {WEEKDAYS_PL.map((wd, i) => (
                        <span
                            key={wd}
                            className={`${styles.weekday} ${i === 5 ? styles.weekdaySat : ""} ${i === 6 ? styles.weekdaySun : ""}`}
                        >
                            {wd}
                        </span>
                    ))}
                </div>

                {/* Day cells */}
                <div className={styles.days}>
                    {cells.map((c, i) => {
                        const isToday = isSameDay(c.date, today);
                        const isSelected = selectedDate && isSameDay(c.date, selectedDate);
                        const dow = c.date.getDay();
                        const isSat = dow === 6;
                        const isSun = dow === 0;
                        const dis = isDayDisabled(c.date);

                        return (
                            <button
                                key={i}
                                type="button"
                                className={[
                                    styles.day,
                                    c.otherMonth ? styles.dayOtherMonth : "",
                                    isToday ? styles.dayToday : "",
                                    isSelected ? styles.daySelected : "",
                                    isSat && !isSelected ? styles.daySat : "",
                                    isSun && !isSelected ? styles.daySun : "",
                                    dis ? styles.dayDisabled : "",
                                ].filter(Boolean).join(" ")}
                                onClick={() => !dis && selectDay(c.date)}
                                disabled={dis}
                            >
                                {c.date.getDate()}
                            </button>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <button className={styles.todayBtn} type="button" onClick={goToday}>
                        Dzisiaj
                    </button>
                    {clearable && value && (
                        <button className={styles.clearBtn} type="button" onClick={handleClear}>
                            Wyczyść
                        </button>
                    )}
                </div>
            </div>
        </>,
        document.body
    ) : null;

    return (
        <>
            <button
                ref={triggerRef}
                id={id}
                type="button"
                className={`${styles.trigger} ${open ? styles.triggerOpen : ""} ${className}`}
                onClick={toggle}
                disabled={disabled}
                data-bledne={ariaInvalid ? "true" : undefined}
                aria-describedby={ariaDescribedBy}
                aria-label={ariaLabel ? `${ariaLabel}: ${value ? formatDisplay(value) : placeholder}` : undefined}
            >
                <span className={styles.triggerIcon}><FiCalendar /></span>
                <span className={`${styles.triggerText} ${!value ? styles.triggerPlaceholder : ""}`}>
                    {value ? formatDisplay(value) : placeholder}
                </span>
                {clearable && value && (
                    <span className={styles.triggerClear} onClick={handleClear} role="button" tabIndex={-1}>
                        <FiX />
                    </span>
                )}
            </button>
            {dropdown}
        </>
    );
}
